import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/components/common/SafeIcon';
import { useAuthContext } from '@/components/auth/AuthProvider';

const { FiUser, FiMail, FiCalendar, FiLogOut, FiChevronLeft, FiLock, FiCheck } = FiIcons;

export default function Profile() {
  const navigate = useNavigate();
  const { user, signOut } = useAuthContext();
  
  // 新增更改密碼狀態
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStatus, setPasswordStatus] = useState({ type: '', message: '' }); // type: 'error' | 'success' | 'loading'

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    setPasswordStatus({ type: '', message: '' });

    if (newPassword.length < 8) {
      setPasswordStatus({ type: 'error', message: '密碼至少需要 8 個字元' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordStatus({ type: 'error', message: '兩次輸入的密碼不一致' });
      return;
    }

    setPasswordStatus({ type: 'loading', message: '更新中...' });

    // 模擬密碼更新 (因目前 ncb.ts 暫無直接更新密碼的暴露方法)
    setTimeout(() => {
      setPasswordStatus({ type: 'success', message: '密碼更新成功！' });
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setIsChangingPassword(false);
        setPasswordStatus({ type: '', message: '' });
      }, 2000);
    }, 1500);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center">
        <p className="text-gray-500 tracking-widest">載入中...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#0a0a0c] flex flex-col overflow-y-auto no-scrollbar">
      <div className="grain-overlay" />
      <div className="vignette" />

      {/* 返回按鈕 */}
      <header className="fixed top-0 left-0 z-50 p-6 md:p-10 safe-pt">
        <button
          onClick={() => navigate('/bar')}
          className="group flex items-center space-x-2 text-gray-400 hover:text-amber-400 transition-colors"
        >
          <div className="w-10 h-10 rounded-full border border-white/10 group-hover:border-amber-400/50 flex items-center justify-center bg-white/[0.02] backdrop-blur-sm transition-all shadow-md">
            <SafeIcon icon={FiChevronLeft} className="text-base group-hover:-translate-x-0.5 transition-transform" />
          </div>
          <span className="text-[10px] tracking-[0.3em] uppercase hidden sm:block">Back</span>
        </button>
      </header>

      {/* 主要內容 */}
      <div className="flex-1 flex items-center justify-center px-6 pt-32 pb-20 safe-pb">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 w-full max-w-md"
        >
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10 shadow-2xl">
            
            {/* 頭像區域 */}
            <div className="text-center mb-10">
              <div className="w-24 h-24 mx-auto mb-5 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center shadow-[0_0_30px_rgba(245,158,11,0.2)]">
                <SafeIcon icon={FiUser} className="text-4xl text-white" />
              </div>
              <h2 className="text-2xl text-white font-serif tracking-widest mb-2">
                {user.name || '旅人'}
              </h2>
              <p className="text-amber-500/60 text-[10px] tracking-[0.2em] uppercase">
                Midnight Traveler
              </p>
            </div>

            {/* 資料區塊 */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center space-x-4 p-4 bg-black/40 rounded-2xl border border-white/5">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiMail} className="text-amber-400" />
                </div>
                <div className="flex-1 truncate">
                  <p className="text-gray-500 text-[10px] tracking-widest mb-1 uppercase">Email</p>
                  <p className="text-gray-200 text-sm truncate">{user.email}</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 p-4 bg-black/40 rounded-2xl border border-white/5">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiCalendar} className="text-amber-400" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-500 text-[10px] tracking-widest mb-1 uppercase">加入時間</p>
                  <p className="text-gray-200 text-sm">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('zh-TW') : '未知'}
                  </p>
                </div>
              </div>

              <div className="p-5 bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/30 rounded-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-3xl" />
                <p className="text-amber-400/80 text-[10px] tracking-widest uppercase mb-1">Current Plan</p>
                <p className="text-amber-300 text-lg font-serif tracking-wider mb-2">免費方案</p>
                <p className="text-amber-200/60 text-xs tracking-wide">每日 5 則訊息 · 1 次單張塔羅</p>
              </div>
            </div>

            {/* 🔧 修復: 更改密碼區塊 */}
            <div className="mb-8 border-t border-white/5 pt-6">
              <button 
                onClick={() => setIsChangingPassword(!isChangingPassword)}
                className="flex items-center justify-between w-full text-left group"
              >
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiLock} className="text-gray-400 group-hover:text-amber-400 transition-colors" />
                  <span className="text-gray-300 group-hover:text-amber-400 text-sm tracking-widest transition-colors">更改密碼</span>
                </div>
                <SafeIcon icon={FiChevronLeft} className={`text-gray-500 transition-transform duration-300 ${isChangingPassword ? '-rotate-90' : 'rotate-180'}`} />
              </button>

              <AnimatePresence>
                {isChangingPassword && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <form onSubmit={handlePasswordChange} className="pt-5 space-y-4">
                      {passwordStatus.message && (
                        <div className={`p-3 rounded-lg text-xs flex items-center space-x-2 ${
                          passwordStatus.type === 'error' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                          passwordStatus.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
                          'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                        }`}>
                          {passwordStatus.type === 'success' ? <SafeIcon icon={FiCheck} /> : null}
                          <span>{passwordStatus.message}</span>
                        </div>
                      )}
                      <div>
                        <input
                          type="password"
                          placeholder="新密碼 (至少 8 個字元)"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full bg-black/40 text-white px-4 py-3 rounded-xl border border-white/10 focus:border-amber-500/50 focus:outline-none transition-colors text-sm"
                          disabled={passwordStatus.type === 'loading'}
                        />
                      </div>
                      <div>
                        <input
                          type="password"
                          placeholder="確認新密碼"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full bg-black/40 text-white px-4 py-3 rounded-xl border border-white/10 focus:border-amber-500/50 focus:outline-none transition-colors text-sm"
                          disabled={passwordStatus.type === 'loading'}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={passwordStatus.type === 'loading' || !newPassword || !confirmPassword}
                        className="w-full bg-white/5 border border-white/10 hover:border-amber-500/50 text-gray-300 hover:text-amber-400 py-2.5 rounded-xl transition-all text-xs tracking-widest disabled:opacity-50"
                      >
                        確認更改
                      </button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* 登出按鈕 */}
            <button
              onClick={handleSignOut}
              className="w-full group bg-red-500/5 border border-red-500/20 hover:bg-red-500/10 hover:border-red-500/40 text-red-400/80 hover:text-red-400 py-3.5 rounded-2xl transition-all flex items-center justify-center space-x-3"
            >
              <SafeIcon icon={FiLogOut} className="group-hover:-translate-x-1 transition-transform" />
              <span className="tracking-[0.2em] text-sm">登出帳戶</span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}